import React from "react";
import { Cell, Pie, PieChart as RechartsPieChart, ResponsiveContainer, Tooltip } from "recharts";
import _ from "lodash";

import { LoadingState, EmptyState } from "./states";
import styles from "./common.module.scss";

const PIE_COLORS = ["#E63462", "#EC6285", "#F291AA", "#F8C0CF", "#DCDCDC"];

export interface PieSlice {
  name: string;
  value: number;
  display?: string;
  color?: string;
}

export interface PieChartProps {
  data?: PieSlice[];
  measureLabel?: string;
  height?: number;
  loading?: boolean;
  variant?: "donut" | "pie";
  showLabels?: boolean;
  showLegend?: boolean;
  colors?: string[];
}

export const PieChart: React.FC<PieChartProps> = ({
  data = [],
  measureLabel = "Value",
  height = 280,
  loading,
  variant = "donut",
  showLabels = true,
  showLegend = true,
  colors = PIE_COLORS,
}) => {
  if (loading) return <LoadingState />;
  if (!data || data.length === 0) return <EmptyState />;

  const slices = [...data]
    .sort((a, b) => b.value - a.value)
    .map((d, i) => ({
      ...d,
      color: d.color || colors[i % colors.length],
    }));

  return (
    <div className={styles.radialPie}>
      <div className={styles.radialPieChart}>
        <ResponsiveContainer width="100%" height={height}>
          <RechartsPieChart margin={{ top: 24, right: 24, bottom: 24, left: 24 }}>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius={variant === "donut" ? "68%" : 0}
              outerRadius="80%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={slices.length > 1 ? 5 : 0}
              cornerRadius={variant === "donut" ? 10 : 0}
              stroke="none"
              labelLine={false}
              label={showLabels ? renderSliceLabel : undefined}
            >
              {slices.map((entry, index) => (
                <Cell key={`${entry.name}-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={(props) => <PieTooltip {...props} measureLabel={measureLabel} />} />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>

      {showLegend && (
        <ul className={styles.radialPieLegend}>
          {slices.map((entry, index) => (
            <li key={`${entry.name}-${index}`}>
              <span className={styles.radialPieDot} style={{ backgroundColor: entry.color }} />
              <span className={styles.radialPieKey}>{entry.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RAD = Math.PI / 180;

const renderSliceLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, payload } = props;
  const text = _.get(payload, "display", _.get(payload, "value", ""));
  if (text === "" || text == null) return null;

  const radius = outerRadius + 16;
  const x = cx + radius * Math.cos(-midAngle * RAD);
  const y = cy + radius * Math.sin(-midAngle * RAD);

  return (
    <text
      x={x}
      y={y}
      fill="#5C5C5C"
      fontSize={13}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {text}
    </text>
  );
};

const PieTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  measureLabel: string;
}> = ({ active, payload, measureLabel }) => {
  if (!active || !payload || !payload.length) return null;
  const datum = _.get(payload, "[0].payload", null);
  if (!datum) return null;

  const value = _.get(datum, "display", _.get(datum, "value", ""));

  return (
    <div className={styles.barChartTooltip}>
      <p className="key">{_.get(datum, "name", "")}</p>
      <p className="value">{`${measureLabel} : ${value}`}</p>
    </div>
  );
};
